import { useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

const label = (target) => target.product_name + (target.variant_label ? ` · ${target.variant_label}` : "");

export default function PendingReorderEditCard({ pending, language, pageSize, active, loading, onAction }) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT(language === "ar" ? "ar" : "en");
  const text = (key, values = {}) => t(`assistant.reorderEdit.${key}`, values);
  const [page, setPage] = useState(1);
  const [chooseAgain, setChooseAgain] = useState(false);
  const target = pending.targets.find((candidate) => candidate.target_id === pending.target_id);
  const choosing = !target || chooseAgain;
  const pages = Math.max(1, Math.ceil(pending.targets.length / pageSize));
  const shown = pending.targets.slice((page - 1) * pageSize, page * pageSize);
  const disabled = !active || loading;
  return <Box component="section" aria-label={text("title")} sx={{ mt: 1, minWidth: 0 }}>
    <Typography variant="body2" fontWeight={800}>{text(`operations.${pending.intent.operation}`)}</Typography>
    {pending.intent.value != null && <Typography variant="body2">{text("value")}: <bdi dir="ltr">{pending.intent.value}</bdi></Typography>}
    {pending.intent.delta != null && <Typography variant="body2">{text("delta")}: <bdi dir="ltr">{pending.intent.delta}</bdi></Typography>}
    {choosing ? <>
      <Typography variant="body2" sx={{ my: 1 }}>{text("choose")}</Typography>
      <Stack spacing={0.7} role="group" aria-label={text("targets")}>
        {shown.map((candidate) => <Button key={candidate.target_id} variant="outlined" size="small" disabled={disabled}
          onClick={() => onAction("select_target", candidate.target_id)} sx={{ textTransform: "none", overflowWrap: "anywhere", justifyContent: "flex-start", textAlign: "start" }}>
          <bdi dir="auto">{label(candidate)}</bdi>
        </Button>)}
      </Stack>
      {pages > 1 && <Stack direction="row" gap={1} justifyContent="space-between" sx={{ mt: 1 }}>
        <Button disabled={page === 1} onClick={() => setPage(page - 1)}>{text("previous")}</Button>
        <Typography variant="caption"><bdi dir="ltr">{page} / {pages}</bdi></Typography>
        <Button disabled={page === pages} onClick={() => setPage(page + 1)}>{text("next")}</Button>
      </Stack>}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{text("selectHelp")}</Typography>
    </> : <>
      <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.7 }}>
        <Trans t={t} i18nKey={`assistant.reorderEdit.${pending.intent.operation === "remove_line" ? "removePreview" : "preview"}`}
          values={{ name: label(target), before: pending.before ?? "—", after: pending.after ?? "—" }}
          components={[<bdi dir="auto" key="name" />, <bdi dir="ltr" key="before" />, <bdi dir="ltr" key="after" />]} />
      </Typography>
      {pending.issue && <Alert severity="warning" sx={{ mt: 1 }}>{text(`issues.${pending.issue}`)}</Alert>}
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
        <Button variant="contained" disabled={disabled || Boolean(pending.issue)} onClick={() => onAction("apply")}>{text("apply")}</Button>
        <Button disabled={disabled} onClick={() => setChooseAgain(true)}>{text("changeTarget")}</Button>
      </Stack>
    </>}
    <Button disabled={disabled} onClick={() => onAction("cancel")} sx={{ mt: 1 }}>{text("cancel")}</Button>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{text("applyHelp")}</Typography>
  </Box>;
}
